import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, Factory, Loader2, Info, Search, Plus, Upload } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { assetService } from '../../../../services/activos/assetService';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useFactory } from '../../../../context/FactoryContext';


function buildHierarchy(assets, rootId = -1) {
  const hierarchies = {};
  if (!assets || !Array.isArray(assets) || assets.length === 0) return hierarchies;

  const normalizeAsset = (asset) => ({
    id: asset.ID !== undefined ? asset.ID : asset.id,
    parentId: asset.ParentID !== undefined ? asset.ParentID : asset.ParentId !== undefined ? asset.ParentId : asset.parentId !== undefined ? asset.parentId : asset.parent_id,
    code: asset.Code !== undefined ? asset.Code : asset.code,
    name: asset.Name !== undefined ? asset.Name : asset.name,
    location: asset.Location !== undefined ? asset.Location : asset.location,
    factory: asset.Factory !== undefined ? asset.Factory : asset.factory,
    ...asset,
  });

  const normalizedAssets = assets.map(normalizeAsset);
  const assetMap = new Map();
  normalizedAssets.forEach((asset) => {
    if (asset.id != null) assetMap.set(asset.id, { ...asset, children: [] });
  });


  let rootNodes = normalizedAssets.filter(
    (asset) => asset.id != null && (asset.parentId == null || asset.parentId === null || asset.parentId === '')
  );
  if (rootId >= 0) {
    rootNodes = normalizedAssets.filter(
      (asset) => asset.id === rootId || asset.id == rootId
    );
  }


  const buildTree = (asset) => {
    const node = assetMap.get(asset.id);
    if (!node) return null;
    const children = normalizedAssets
      .filter((child) => child.parentId != null && child.parentId === asset.id)
      .map((child) => buildTree(child))
      .filter(Boolean);
    if (children.length > 0) node.children = children;
    return node;
  };

  const sortChildren = (node) => {
    if (node.children?.length) {
      node.children.sort((a, b) =>
        (a.code || '').toString().localeCompare((b.code || '').toString(), undefined, { numeric: true, sensitivity: 'base' })
      );
      node.children.forEach(sortChildren);
    }
  };

  rootNodes.forEach((rootAsset) => {
    const tree = buildTree(rootAsset);
    if (tree) {
      const factoryCode = rootAsset.code || rootAsset.factory || `ROOT_${rootAsset.id}`;
      hierarchies[factoryCode] = tree;
    }
  });
  Object.values(hierarchies).forEach(sortChildren);
  return hierarchies;
}

function getPathToAsset(hierarchies, assetId) {
  if (!hierarchies || !assetId) return [];
  const targetId = Number(assetId);

  const path = [];

  const dfs = (node) => {
    if (!node) return false;
    path.push(node);
    if (Number(node.id) === targetId) {
      return true;
    }
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        if (dfs(child)) return true;
      }
    }
    path.pop();
    return false;
  };

  for (const root of Object.values(hierarchies)) {
    path.length = 0;
    if (dfs(root)) {
      return [...path];
    }
  }

  return [];
}

export const PlansHierarchy = ({ assetId, setAssetId, setIsLoadingAsset, setIsLoadingSensorAsset, setAssetDetails, selectMode, rootAssetId = -1 }) => {
  const queryClient = useQueryClient();
  const { permissions, checkFactoryAccess } = usePermissions();
  const { selectedFactory } = useFactory();

  const [expandedNodes, setExpandedNodes] = useState(new Set());
  //const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [assetToEdit, setAssetToEdit] = useState(null);

  useEffect(() => {
    setAssetId(null);
    setExpandedNodes(new Set());
    setSearchQuery('');
  }, [selectedFactory, rootAssetId]);

  const { data: assets = [], isLoading: loading, isError: isError, error } = useQuery({
    queryKey: ['assets'],
    queryFn: () => (assetService.getAssets()),
    retry: 1,
  });

  const hierarchies = useMemo(() => {
    const allHierarchies = buildHierarchy(assets, rootAssetId);
    if (rootAssetId >= 0) {
      return allHierarchies;
    }
    if (selectedFactory && allHierarchies[selectedFactory]) {
      return { [selectedFactory]: allHierarchies[selectedFactory] };
    }
    return allHierarchies;
  }, [assets, selectedFactory, rootAssetId]);

  const {
    data: selectedAssetDetails,
    isLoading: loadingDetails,
  } = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => assetService.getAsset(assetId),
    enabled: !!assetId,
    retry: 1,
  });

  useEffect(() => {
    setAssetDetails(selectedAssetDetails);
    selectMode === 'sensor' ?
      (selectedAssetDetails !== (null || undefined) ? setIsLoadingSensorAsset(false) : null) :
      (selectedAssetDetails !== (null || undefined) ? setIsLoadingAsset(false) : null);
  }, [selectedAssetDetails])



  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const expandAll = () => {
    const ids = new Set();
    const collect = (n) => {
      ids.add(n.id);
      n.children?.forEach(collect);
    };
    Object.values(hierarchies).forEach(collect);
    setExpandedNodes(ids);
  };

  const collapseAll = () => setExpandedNodes(new Set());

  const getLevelColor = (level) => {
    switch (level) {
      case 0: return 'text-blue-400';
      case 1: return 'text-green-400';
      case 2: return 'text-yellow-400';
      default: return 'text-gray-300';
    }
  };

  const handleAssetSelect = (assetId, event) => {
    if (event?.target?.closest?.('.detail-button')) {
      event.preventDefault();
      event.stopPropagation();
    }
    selectMode === 'sensor' ? setIsLoadingSensorAsset(true) :
      setIsLoadingAsset(true);
    setAssetId(assetId);
  };


  const filterNode = (node) => {
    if (!node) return null;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches = (node.code || '').toLowerCase().includes(q) || (node.location || '').toLowerCase().includes(q);
      if (matches) return { ...node, children: node.children?.map(filterNode).filter(Boolean) };
      if (node.children?.length) {
        const filtered = node.children.map(filterNode).filter(Boolean);
        if (filtered.length) return { ...node, children: filtered };
      }
      return null;
    }
    return { ...node, children: node.children?.map(filterNode).filter(Boolean) };
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = assetId === node.id;
    const indent = level * 16;
    const basePadding = 5;


    return (
      <div key={node.id}>
        <div
          className={`flex items-center gap-1 px-1 py-0.5 hover:bg-gray-800/50 rounded cursor-pointer group transition-colors ${isSelected ? 'bg-blue-500/20 border-l-2 border-blue-500' : ''
            }`}
          style={{ paddingLeft: `${basePadding + indent}px` }}
          onClick={() => {
            if (hasChildren) toggleNode(node.id);
            handleAssetSelect(node.id);
          }}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown size={12} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={12} className="text-gray-400 flex-shrink-0" />
          ) : (
            <div className="w-3 flex-shrink-0" />
          )}
          <span
            className={`flex-1 min-w-0 truncate text-xs transition-colors ${isSelected ? 'text-blue-400 font-semibold' : getLevelColor(level)
              }`}
            onClick={(e) => {
              e.stopPropagation();
              handleAssetSelect(node.id, e);
            }}
          >
            {node.code}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>{node.children.map((child) => renderTreeNode(child, level + 1))}</div>
        )}
      </div>
    );
  };

  const renderFullTree = () => {
    return (
      <div className="space-y-0.5">
        {Object.keys(hierarchies)
          .sort()
          .map((factoryCode) => {
            const factory = hierarchies[factoryCode];
            const filtered = filterNode(factory);
            return filtered ? renderTreeNode(filtered, 0) : null;
          })}
      </div>
    );
  };

  if (Object.keys(hierarchies).length === 0 && !loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center text-gray-500">
          <p className="text-lg mb-4">No hay datos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-white "
    >

      <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
            <Factory size={16} />
            Jerarquía {selectMode === 'sensor' ? 'Sensores' : ''}
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">Selecciona un activo {selectMode === 'sensor' ? 'sensor' : ''}</p>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={expandAll} className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors" title="Expandir todo">
            <ChevronDown size={14} />
          </button>
          <button type="button" onClick={collapseAll} className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors" title="Colapsar todo">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      <div className="px-2 py-1.5 border-b border-gray-800">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-7 pr-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {renderFullTree()}
      </div>

    </div>

  );


};
